import { useFeature } from "@optimizely/react-sdk";
import React, { useContext } from "react";
import { SelectedTenantContext } from "polyrouter/SelectedTenantProvider";
import { useAuth0 } from "@auth0/auth0-react";
import { VariableValuesObject } from "@optimizely/react-sdk/dist/client";

export enum FEATURE_FLAGS {
  ACCOUNT_CONSOLIDATION = "account-consolidation",
  ANALYSIS_CHARTS_AGGREGATE_VALUE = "analysis-charts-aggregate-value",
  CALL_CONTROL = "call_control",
  DEBUG_USER_ROLES = "user-roles-debug",
  // TODO: enabled for everyone - remove related code and this flag after 8/15/2022 if still enabled
  DEV_CONFIG_OVERRIDES = "dev_config_overrides",
  DOWNLOAD_LOGS = "download_logs",
  DOWNLOADING_PCAP = "downloading_pcap",
  EDGE_RELAY = "edge-relay",
  INSIGHTS_CHARTS = "insight-charts",
  INSIGHTS_DEVICE_LIST = "lens-insight-device-list",
  INSIGHTS_LEGACY = "legacy-insight-details",
  INSIGHTS_ROOM_CATEGORIES = "room-insights-categories",
  INTEGRATION_AZURE_AD = "integration-azure-ad",
  IS_POLY_USER = "poly-users-flag",
  LENS_LANGUAGE_SELECT = "lens-language-select",
  MOBILE_APP_REGISTRATION_TOAST = "mobile_app_registration_toast",
  MOTD_TOAST_ENABLED = "motd_toast_enabled",
  POLYOS_LOGGING_TO_FLASH = "polyos_logging_to_flash",
  POLYOS_MANAGEMENT = "polyos_management",
  RELAY_RPRM = "relay_rprm",
  // TODO: enabled for everyone - remove related code and this flag after 8/15/2022 if still enabled
  RESOURCE_FILES = "resource_files",
  SITE_REBOOT = "site_reboot",
  SITE_TOPOLOGY_IMPORT = "site_topology_import",
  // TODO: enabled for everyone - remove related code and this flag after 8/15/2022 if still enabled
  TAC_INTEGRATION = "tac-integration",
  MANAGE_CALL_STATUS = "manage-call-status"
}

/**
 * Abstraction of OptimizelyFeature component.
 * 1. Simplifies usage.
 * 2. Allows for easier replacement of Optimizely in the future.
 *
 * == USAGE ==
 *
 * === Basic ===
 * <FeatureFlagEnabled feature="some-awesome-feature">
 *     <SomeAwesomeComponentOrJSX .... />
 * </FeatureFlagEnabled>
 *
 * === Else Condition ===
 * When you need to render something if the feature is not enabled,
 * leverage the elseRender argument (defaults to null):
 *
 * <FeatureFlagEnabled feature="some-awesome-feature" elseRender={<SomeOtherComponentOrJSX />}>
 *     <SomeAwesomeComponentOrJSX .... />
 * </FeatureFlagEnabled>
 *
 * === And Condition ===
 * When multiple flags must be enabled, simply nest the FeatureFlagEnabled
 * component:
 *
 * <FeatureFlagEnabled feature="some-awesome-feature">
 *     <FeatureFlagEnabled feature="user-has-permission">
 *        <SomeAwesomeComponent .... />
 *     </FeatureFlagEnabled>
 * </FeatureFlagEnabled>
 *
 * === One Or The Other Features ===
 * When something should render if one of two flags are enabled (OR condition):
 *
 * <FeatureFlagsOrEnabled feature="if-this-feature" orFeature="or-that-feature">
 *     <ComponentRendersIfEitherFeatureIsEnabled />
 * </Feature>
 *
 * === Feature Disabled ===
 * There's two options to render something if a feature is disabled.
 *
 * Either:
 * <FeatureFlagDisabled feature="some-awesome-feature">
 *     <SorryYouDontHaveAccessComponent />
 * </FeatureFlagDisabled>
 *
 * Or:
 * <FeatureFlagEnabled feature="some-awesome-feature" elseRender={<SorryYouDontHaveAccessComponent />} />
 *
 * === Complex Disabled Example ===
 *
 * <FeatureFlagsOrEnabled feature="if-this-feature" orFeature="or-that-feature" elseRender={<SorryComponent />} />
 *
 */

export interface FeatureFlagProps {
  feature: keyof typeof FEATURE_FLAGS;
  andIf?: boolean;
  orIf?: boolean;
  elseRender?: React.ReactNode;
}

export function useFeatureFlagWithTenant(
  feature: keyof typeof FEATURE_FLAGS
): [boolean, VariableValuesObject, boolean, boolean] {
  const { tenant } = useContext(SelectedTenantContext);
  const { user } = useAuth0();
  const overrideAttributes: { tenantId?: string; email?: string } = {};
  if (tenant?.id) {
    overrideAttributes.tenantId = tenant.id;
  }
  if (user?.email) {
    overrideAttributes.email = user.email;
  }
  const [isEnabled, variables, clientReady, didTimeout] = useFeature(
    FEATURE_FLAGS[feature],
    {},
    {
      overrideAttributes: overrideAttributes
    }
  );

  return [isEnabled, variables, clientReady, didTimeout];
}

/**
 * Render the children if the feature flag is enabled.
 * Additionally, accepts an "andIf" and / or "orIf" boolean
 * to combine the logic with.
 *
 * andIf: requires the enabled AND andIf to render.
 * orIf: requires the enabled OR orIf to render.
 * Both: not recommended, use orIf instead.
 *
 * @param props
 * @constructor
 */
export const FeatureFlagEnabled: React.FC<FeatureFlagProps> = props => {
  const [andIf, orIf] = prepFeatureFlagIfs(props);
  const [isEnabled] = useFeatureFlagWithTenant(props.feature);

  if ((isEnabled && andIf) || orIf) {
    return <>{props.children}</>;
  }

  return <>{props.elseRender || null}</>;
};

/**
 * Renders the children if EITHER of the two flags are enabled.
 * PRO TIP:
 * Can be used to render something if NEITHER of two flags is enabled
 * by leveraging the `elseRender` property.
 *
 * orFeature: the second feature to check.
 * andIf: requires EITHER of the flags to be enabled, AND andIf to render.
 * orIf: requires EITHER one of the flags to be enabled OR orIf to render.
 * Both: not recommended, use orIf instead.
 *
 * @param props
 * @constructor
 */
export const FeatureFlagsOrEnabled: React.FC<FeatureFlagProps & {
  orFeature: keyof typeof FEATURE_FLAGS;
}> = props => {
  const [andIf, orIf] = prepFeatureFlagIfs(props);
  const [isEnabled] = useFeatureFlagWithTenant(props.feature);

  return (
    <FeatureFlagEnabled
      feature={props.orFeature}
      andIf={andIf}
      orIf={isEnabled || orIf}
      elseRender={props.elseRender}
    >
      {props.children}
    </FeatureFlagEnabled>
  );
};

/**
 * Renders the children if the feature flag is disabled.
 * Additionally, accepts an "andIf" and / or "orIf" boolean
 * to combine the logic with.
 *
 * andIf: requires the enabled to be FALSE AND andIf to render.
 * orIf: requires the enabled to be FALSE OR orIf to render.
 *
 * @param props
 * @constructor
 */
export const FeatureFlagDisabled: React.FC<FeatureFlagProps> = props => {
  const [andIf, orIf] = prepFeatureFlagIfs(props);
  const [isEnabled] = useFeatureFlagWithTenant(props.feature);

  if ((!isEnabled && andIf) || orIf) {
    return <>{props.children}</>;
  }

  return null;
};

const prepFeatureFlagIfs = (props: FeatureFlagProps) => {
  // for logic to work, default to TRUE if not provided
  const andIf = props.andIf === undefined || props.andIf;
  // for logic to work, default to FALSE if not provided
  const orIf = props.orIf !== undefined && props.orIf;

  return [andIf, orIf];
};
